import { useMemo, useRef, useState } from 'react';
import orderBy from 'lodash/orderBy';
import type { ApolloQueryResult } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useProperty, useSiteContext, useSnowplowTracking, useTranslation } from '@eti/providers';
import { Helmet } from 'react-helmet';
import type { PaymentDataOnPaymentPageQuery } from '@eti/schema-types';
import Loading from '../../../common/loading/components/Loading';
import { doAsyncFormValidation } from '../utils/asyncFormValidation';
import {
  getInitialFormValuesForAddressFormSection,
  setMethodInitialValues,
} from '../utils/initialValues';
import { getTravelersDisplayPrices } from '../../../utils/format';
import PaymentPageErrorDelegate from '../entry-page/exception-handling/PaymentPageErrorDelegate';
import useValidationTexts from '../hooks/useValidationTexts';
import { generateInitialValuesAncillaryBundles } from '../../traveler-details/utils/bundles';
import { transformProducts } from '../entry-page/utils/ancillaryProducts';
import { REJECT_PRICE_CHANGE } from '../graphql/mutations';
import { PAYMENT_DATA_QUERY } from '../graphql/queries';
import {
  getPaymentMethodPrices,
  sortPaymentMethods,
  transformPaymentMethods,
} from '../utils/paymentMethods';
import useRefetchOnPageshow from '../hooks/useRefetchOnPageshow';
import useRefetchOnVisibilityChange from '../hooks/useRefetchOnVisibilityChange';
import { stepIndicatorDataAdapter } from '../../../common/step-indicator-v2/utils';
import useCountries from '../hooks/useCountries';
import type { PaymentPageContainerProps } from './PaymentPageContainer';
import PaymentPageContainer from './PaymentPageContainer';
import PaymentPageProvider from './PaymentPageProvider';

type MapPropsForPaymentType = Omit<
  Extract<PaymentPageContainerProps, { isPaymentLinkCart?: false | never }>,
  | 'headerAndInfoHeight'
  | 'isSnowplowTrackingEnabled'
  | 'onDeclineTripChange'
  | 'sendTrackEvent'
  | 'setHasPendingPayment'
  | 'setIsMenuVisible'
  | 'validationTexts'
  | 'client'
  | 'isTaxIdValidationEnabled'
  | 'taxIdValidationFailedMessage'
  | 'setIsTaxIdValidating'
  | 'isTaxIdValidating'
>;

const mapPropsForPayment = (
  paymentQueryData: PaymentDataOnPaymentPageQuery,
  refetchFunction: () => Promise<ApolloQueryResult<PaymentDataOnPaymentPageQuery>>,
  siteContext: ReturnType<typeof useSiteContext>,
  countries: ReturnType<typeof useCountries>['countries'],
  getProperty: ReturnType<typeof useProperty>['p'],
  getTranslation: ReturnType<typeof useTranslation>['t'],
): MapPropsForPaymentType => {
  const { cart, paymentData } = paymentQueryData;
  const { brand, market, siteCurrency } = siteContext;
  const {
    isPanAndDoBDisabled,
    transactionId,
    availablePaymentMethods: paymentMethods,
    cadillac,
  } = paymentData;

  const {
    ancillaryBundles,
    chargeCurrencyPriceFormat,
    customerEmail,
    customerPhone,
    customerContactInfo,
    discountPrice: discountAmount,
    hasPotentialForAdditionalTax,
    id: cartId,
    priceInChargeCurrency,
    products,
    serviceFee,
    subtotalPrice,
    trips,
  } = cart ?? {};

  const trip = trips?.at(0);

  const {
    bounds,
    cabinBaggageTripSummary,
    changes: tripChanges,
    checkedBaggageSummary,
    fareRules,
    personalItemTripSummary,
    travelerPrices,
    travelers,
    tripCharacteristics,
    type,
    includedExtraProducts,
    includedCabinBaggage,
  } = trip ?? {};

  const isOtherChargeCurrency =
    priceInChargeCurrency && priceInChargeCurrency?.price?.currency?.code !== siteCurrency.code;

  const preloadReceiptPage = async (inView: boolean) => {
    const ReceiptPage = inView && (await import('../../receipt'));

    if (ReceiptPage) {
      ReceiptPage.default.preload();
    }
  };

  const sortedPaymentMethods = sortPaymentMethods(
    transformPaymentMethods(paymentMethods),
    getProperty('IbeClient.Payment.PaymentTypeDisplayOrder'),
  );
  const paymentMethodPrices = getPaymentMethodPrices(sortedPaymentMethods, getTranslation);
  const isFirstPaymentMethodOpen = getProperty('Payment.FirstPaymentMethod.Open.Enabled');
  const { bundles } = generateInitialValuesAncillaryBundles(
    ancillaryBundles ?? [],
    bounds,
    getTranslation,
    travelers,
  );

  return {
    asyncValidate: doAsyncFormValidation,
    bounds,
    brand,
    bundles,
    cabinBaggageTripSummary,
    cadillac,
    cartId,
    chargeCurrencyPriceFormat,
    checkedBaggageSummary,
    customerContactInfo,
    customerEmail,
    customerPhone,
    discountAmount: discountAmount?.price?.value,
    fareRules: fareRules ?? [],
    hasPotentialForAdditionalTax,
    includedCabinBaggage,
    includedExtraProducts,
    initialValues: {
      ...getInitialFormValuesForAddressFormSection(market, countries),
      ...setMethodInitialValues(sortedPaymentMethods, isFirstPaymentMethodOpen),
    },
    isMultiStop: type !== 'ONE_WAY' && type !== 'RETURN',
    isOtherChargeCurrency,
    isPanAndDoBDisabled,
    market: market.code,
    paymentMethodPrices,
    paymentMethods: sortedPaymentMethods,
    personalItemTripSummary,
    preloadReceiptPage,
    products: transformProducts(getTranslation, products ?? [], travelers, bounds),
    refetchPaymentQuery: refetchFunction,
    selectionKey: trip?.selectionKey,
    serviceFee,
    states: orderBy(market.states, 'label', 'asc'),
    subtotalPrice: subtotalPrice?.price?.value,
    transactionId,
    travelerPrices: getTravelersDisplayPrices(travelers, travelerPrices),
    travelers,
    tripChanges: tripChanges ?? [],
    tripCharacteristics,
    tripProviderSystems: trip?.systems,
  };
};

interface PaymentPageGraphqlContainerProps {
  headerAndInfoHeight: number;
  setIsMenuVisible: (pathname: { type: string }) => void;
}

const PaymentPageGraphqlContainer = (props: PaymentPageGraphqlContainerProps) => {
  const hasFetchedPaymentData = useRef(false);

  const { error, data, loading, refetch } = useQuery(PAYMENT_DATA_QUERY, {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      hasFetchedPaymentData.current = true;
    },
  });

  const { countries, loading: countriesLoading } = useCountries();

  const [rejectPriceChange, { called: rejectPriceCalled }] = useMutation(REJECT_PRICE_CHANGE);

  const onDeclineTripChange = async () => {
    await rejectPriceChange().then(() => refetch());
  };

  const { p } = useProperty();
  const { t } = useTranslation();
  const validationTexts = useValidationTexts();
  const siteContext = useSiteContext();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [isTaxIdValidating, setIsTaxIdValidating] = useState(false);
  const client = useApolloClient();
  const taxIdLabel = p('Payment.TaxIdFeature.Label');
  const isTaxIdValidationEnabled = p('Payment.PanNumberValidation.Enabled');
  const taxIdValidationFailedMessage = t('ValidationMessages.TaxIdValidationFailed', taxIdLabel);
  const adaptedPaymentPageData = stepIndicatorDataAdapter(
    data,
    t('IbeClient.StepIndicator.Results.Title'),
    t('IbeClient.StepIndicator.Payment.Title'),
    true,
  );

  const propsForPayment = useMemo(
    () =>
      adaptedPaymentPageData &&
      mapPropsForPayment(adaptedPaymentPageData, refetch, siteContext, countries, p, t),
    [adaptedPaymentPageData, refetch, siteContext, countries, p, t],
  );

  useRefetchOnPageshow(refetch, hasPendingPayment);
  useRefetchOnVisibilityChange(refetch, hasPendingPayment);

  if (loading || countriesLoading) {
    return <Loading />;
  }

  if (error) {
    return <PaymentPageErrorDelegate error={error} refetch={refetch} />;
  }

  if (
    !hasFetchedPaymentData.current &&
    !rejectPriceCalled &&
    adaptedPaymentPageData?.cart?.state === 'PRICE_CHANGE'
  ) {
    rejectPriceChange();
  }
  return (
    <>
      <Helmet title={t('PageTitle.rf.payment.action')} />
      {propsForPayment && (
        <PaymentPageProvider paymentMethods={propsForPayment.paymentMethods}>
          <PaymentPageContainer
            {...propsForPayment}
            client={client}
            headerAndInfoHeight={props.headerAndInfoHeight}
            isSnowplowTrackingEnabled={isSnowplowTrackingEnabled}
            isTaxIdValidating={isTaxIdValidating}
            isTaxIdValidationEnabled={isTaxIdValidationEnabled}
            onDeclineTripChange={onDeclineTripChange}
            sendTrackEvent={sendTrackEvent}
            setHasPendingPayment={setHasPendingPayment}
            setIsMenuVisible={props.setIsMenuVisible}
            setIsTaxIdValidating={setIsTaxIdValidating}
            taxIdValidationFailedMessage={taxIdValidationFailedMessage}
            validationTexts={validationTexts}
          />
        </PaymentPageProvider>
      )}
    </>
  );
};

export default PaymentPageGraphqlContainer;
